<template>
  <div class="flex items-start gap-2 w-full">
    <div>
      <user-avatar v-if="item.user" :user="item.user" :size="8" />
      <div
        v-else
        class="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100"
      >
        <div class="text-xs font-medium text-gray-600">F</div>
      </div>
    </div>
    <div class="flex flex-col w-full">
      <div class="flex items-center gap-x-2 flex-wrap">
        <span class="text-sm font-medium">{{ causer }}</span>
        <span class="flex gap-2 items-center">
          <time-ago
            relative
            :date="date"
            :locale="locale"
            class="text-xs text-gray-500"
          />
          <span v-if="props.item.comment" class="text-xs text-gray-500">{{
            "-"
          }}</span>
          <span class="text-xs text-gray-500" v-if="props.item.comment">
            {{
              props.item.comment.type === "internal"
                ? t("validation.values.comment.internal")
                : t("validation.values.comment.public_reply")
            }}
          </span>
        </span>
      </div>
      <div class="text-sm mt-1 text-gray-600 w-full">
        <i18n-t
          scope="global"
          :keypath="'tasks.activity.' + props.item.description"
          tag="p"
          v-if="!props.item.comment"
        >
          <template v-slot:number>{{ props.task.number }}</template>
          <template
            v-slot:time
            v-if="props.item.start_date && props.item.end_date"
          >
            {{
              differenceInMinutes(
                new Date(props.item.end_date),
                new Date(props.item.start_date)
              )
            }}
          </template>
          <template v-slot:logged_for v-if="props.item.logged_for">
            {{ props.item.logged_for.fullname }}
          </template>
          <template v-slot:time_description v-if="props.item.time_description">
            "{{ props.item.time_description }}"
          </template>
          <template
            v-slot:material_description
            v-if="props.item.material_description"
          >
            "{{ props.item.material_description }}"
          </template>
          <template v-slot:cost v-if="props.item.cost">
            {{ formatter.format(props.item.cost) }}
          </template>
          <template v-slot:date v-if="logDate">
            {{ new Date(logDate).toLocaleDateString() }}
          </template>
          <template v-slot:category v-if="props.item.category"
            >{{ props.item.category.name }}
          </template>
          <template v-slot:priority v-if="props.item.priority"
            >{{ props.item.priority.name }}
          </template>
          <template v-slot:scheduled_for v-if="item.scheduled_for"
            >{{ scheduledFor }}
          </template>
          <template v-slot:assignees v-if="props.item.assignees">
            {{ assignees }}
          </template>
          <template v-slot:visibility>
            {{ visibility }}
          </template>
          <template v-slot:previous_description v-if="props.item.title">
            "{{ props.item.title.previous_description }}"
          </template>
          <template v-slot:current_description v-if="props.item.title">
            "{{ props.item.title.current_description }}"
          </template>
          <template v-slot:previous_space v-if="props.item.space">
            {{ props.item.space.previous_space.name }}
          </template>
          <template v-slot:current_space v-if="props.item.space">
            {{ props.item.space.current_space.name }}
          </template>
        </i18n-t>
        <div
          class="grid grid-cols-3 gap-2 mt-2"
          v-if="otherDocuments.length > 0"
        >
          <div
            v-for="document in otherDocuments"
            :key="document.id"
            class="cursor-pointer flex space-x-1 rounded-md border flex items-center justify-center bg-white p-2 overflow-hidden"
            @click="downloadDocument(document.urls.file)"
          >
            <FileFive class="w-6 h-6 shrink-0" />
            <span class="truncate text-ellipsis text-gray-700 font-medium">
              {{ document.name }}
            </span>
          </div>
        </div>
        <div class="grid grid-cols-3 gap-2 mt-2" v-if="otherImages.length > 0">
          <div v-for="image in otherImages" :key="image.id">
            <img
              class="rounded-md border cursor-pointer"
              :src="image.urls.thumbnail"
              alt=""
              @click="previewOtherImage(image.urls.large)"
            />
          </div>
        </div>

        <div
          v-if="props.item.comment"
          class="flex flex-col space-y-2 border break-all border-gray-200 bg-white text-gray-900 font-normal rounded-xl p-3 w-full"
        >
          <div v-html="content" v-if="content"></div>

          <div
            class="grid grid-cols-3 gap-2"
            v-if="commentDocuments.length > 0"
          >
            <div
              v-for="document in commentDocuments"
              :key="document.id"
              class="cursor-pointer flex space-x-1 rounded-md border flex items-center justify-center bg-white p-2 overflow-hidden"
              @click="downloadDocument(document.urls.file)"
            >
              <FileFive class="w-6 h-6 shrink-0" />
              <span class="truncate text-ellipsis text-gray-700 font-medium">
                {{ document.name }}
              </span>
            </div>
          </div>

          <div
            class="grid grid-cols-3 gap-2 mt-2"
            v-if="commentImages.length > 0"
          >
            <div v-for="image in commentImages" :key="image.id">
              <img
                class="rounded-md border cursor-pointer"
                :src="image.urls.thumbnail"
                alt=""
                @click="previewCommentImage(image.urls.large)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <MediaViewer
      :open="openCommentImageViewer"
      :url="commentImageBeingViewed"
      @close="openCommentImageViewer = false"
      @next="nextCommentImage"
      @previous="previousCommentImage"
      :multiple="commentImages.length > 1"
    />
    <MediaViewer
      :open="openOtherImageViewer"
      :url="otherImageBeingViewed"
      @close="openOtherImageViewer = false"
      @next="nextOtherImage"
      @previous="previousOtherImage"
      :multiple="otherImages.length > 1"
    />
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, ref } from "vue";
import UserAvatar from "@/Tailwind/Components/UserAvatar";
import { usePage } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";
import { useUTC } from "@/Tailwind/Mixins/useUTC";
import TimeAgo from "@/Tailwind/Components/TimeAgo";
import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import FileFive from "@/Tailwind/Icon/FileFive";
import { format } from "date-fns";
import MediaViewer from "@/Tailwind/Components/MediaViewer.vue";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { mobileSatisfiesVersion } from "@/Tailwind/Helpers/version";
import { differenceInMinutes } from "date-fns";

const { t } = useI18n();
const { fromUTC } = useUTC();

const props = defineProps({
  item: Object,
  task: Object,
});

const formatter = new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR",
});

const locale = usePage().props.locale;

const commentImageBeingViewed = ref("");
const openCommentImageViewer = ref(false);

const otherImageBeingViewed = ref("");
const openOtherImageViewer = ref(false);

function previewCommentImage(url) {
  commentImageBeingViewed.value = url;
  openCommentImageViewer.value = true;
}

function previewOtherImage(url) {
  otherImageBeingViewed.value = url;
  openOtherImageViewer.value = true;
}

const { runningInWebView, openURL } = useWebView();
const downloadDocument = (url) => {
  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  // Fallback for outdated mobile apps.
  window.open(url, "_blank");
};

const otherImages = computed(() => {
  const files = props.item.documents;
  if (!files) {
    return [];
  }

  return props.item.documents.filter((document) =>
    ["image/png", "image/jpg", "image/jpeg"].includes(document.mime_type)
  );
});

const commentImages = computed(() => {
  const files = props.item.comment?.documents;
  if (!files) {
    return [];
  }

  return props.item.comment?.documents.filter((document) =>
    ["image/png", "image/jpg", "image/jpeg"].includes(document.mime_type)
  );
});

const otherDocuments = computed(() => {
  const files = props.item.documents;
  if (!files) {
    return [];
  }

  return props.item.documents.filter(
    (document) =>
      !["image/png", "image/jpg", "image/jpeg"].includes(document.mime_type)
  );
});

const commentDocuments = computed(() => {
  const files = props.item.comment?.documents;
  if (!files) {
    return [];
  }

  return props.item.comment?.documents.filter(
    (document) =>
      !["image/png", "image/jpg", "image/jpeg"].includes(document.mime_type)
  );
});

const activeUserId = usePage().props.auth.user.id;

const content = computed(() => {
  return generateHTML(props.item.comment.contents, [
    StarterKit,
    Link,
    Mention.configure({
      HTMLAttributes: {
        class:
          "before:content-['@'] before:text-gray-800 before:font-light font-medium text-brand-800 inline-block",
      },
      renderLabel({ node }) {
        return `${node.attrs.label ?? node.attrs.id}`;
      },
    }),
  ]);
});

const causer = computed(() => {
  const { messenger, user, description } = props.item;

  if (
    description === "priority_detected" ||
    description === "category_detected"
  ) {
    return t("components.fixform");
  }

  if (user) {
    if (user.id === usePage().props.auth.user.id) {
      return t("components.you");
    }

    if (user.role === "service_provider") {
      if (user.firstname) {
        return `${user.company_name} (${user.firstname})`;
      } else {
        return user.company_name;
      }
    }

    return user.company_name || user.fullname || user.email;
  }

  if (messenger) {
    const name = messenger.name ? `${messenger.name} - ` : null ?? "";

    if (messenger.email && messenger.mobile) {
      return `${name} ${messenger.email} (${messenger.mobile})`.trim();
    }

    if (messenger.email) {
      return `${name} ${messenger.email}`.trim();
    }

    if (messenger.mobile) {
      return `${name} ${messenger.mobile}`.trim();
    }
  }

  if (isRecurringTask.value || isReminder.value) {
    return t("components.fixform");
  }

  return t("components.anonymous");
});

const isRecurringTask = computed(() => {
  return props.task.recurrence !== null;
});

const date = computed(() => {
  return fromUTC(props.item.created_at);
});

const logDate = computed(() => {
  const date = props.item.start_date || props.item.date;
  if (!date) {
    return;
  }

  return fromUTC(date);
});

const scheduledFor = computed(() => {
  return format(fromUTC(props.item.scheduled_for), "dd MMM YYY");
});

const assignees = computed(() => {
  const [assignee] = props.item.assignees;

  if (!assignee) {
    return false;
  }

  if (assignee.role === "service_provider") {
    if (assignee.firstname) {
      return `${assignee.company_name} (${assignee.firstname})`;
    } else {
      return assignee.company_name;
    }
  }

  return assignee.fullname;
});

const visibility = computed(() => {
  return props.item.private ? "private" : "public";
});

const isReminder = computed(() => {
  return props.task.reminder;
});

onMounted(() => {
  Array.from(document.querySelectorAll("[data-id]"))
    .filter((el) => {
      return el.dataset.id === activeUserId;
    })
    .forEach((el) => el.classList.add("selected"));
});

const nextCommentImage = () => {
  const index = commentImages.value.findIndex(
    (photo) => photo.urls.large === commentImageBeingViewed.value
  );

  // TODO Check if resource needs to be addapted like tasks detail

  if (index === commentImages.value.length - 1) {
    commentImageBeingViewed.value = commentImages.value[0].urls.large;
  } else {
    commentImageBeingViewed.value = commentImages.value[index + 1].urls.large;
  }
};

const previousCommentImage = () => {
  const index = commentImages.value.findIndex(
    (photo) => photo.urls.large === commentImageBeingViewed.value
  );

  if (index === 0) {
    commentImageBeingViewed.value =
      commentImages.value[commentImages.value.length - 1].urls.large;
  } else {
    commentImageBeingViewed.value = commentImages.value[index - 1].urls.large;
  }
};

const nextOtherImage = () => {
  const index = otherImages.value.findIndex(
    (photo) => photo.urls.large === otherImageBeingViewed.value
  );

  // TODO Check if resource needs to be addapted like tasks detail

  if (index === otherImages.value.length - 1) {
    otherImageBeingViewed.value = otherImages.value[0].urls.large;
  } else {
    otherImageBeingViewed.value = otherImages.value[index + 1].urls.large;
  }
};

const previousOtherImage = () => {
  const index = otherImages.value.findIndex(
    (photo) => photo.urls.large === otherImageBeingViewed.value
  );

  if (index === 0) {
    otherImageBeingViewed.value =
      otherImages.value[otherImages.value.length - 1].urls.large;
  } else {
    otherImageBeingViewed.value = otherImages.value[index - 1].urls.large;
  }
};
</script>

<style lang="scss">
.selected {
  @apply font-bold before:font-normal;
}
</style>
